/* eslint-disable import/order */
import 'preact/debug-conditional';

import MojitoCore from 'mojito/core';
import MojitoApplications from 'mojito/applications';
import MojitoServices from 'mojito/services';
import MojitoNGEN from 'mojito/ngen';

import './utils/dinosaur-detector.js';
import {ApplicationConfig} from '#core/config/application-config.js';
import {AbstractFeature, allFeatures} from './application/abstract-feature.js';
import {API_MESSAGES, EXTERNAL_METHODS, IMS_TYPES, INITIALIZATION_STATE} from './application/api/types.js';
import {Localization} from '#core/localization.js';
import {Logger} from '#core/utils/logger.js';
import {assignRecursively, mergeTo, setObjectProperty} from '#core/utils/config-utils.js';
import '#core/application/index.sportsbook.js';
import {init, mount, sendMessage, syncSession, terminate, unmount} from '#core/application/application.js';
import '#core/application/debug/index.js';
import '#core/features/sentry/index.js';
import '#core/features/tracer/index.js';
import {WidgetsProviderService} from '#core/application/widgets-provider/index.js';
import '#core/features/playground/index.js'; // Should have the highest priority and imported after widgets-provider
import '#core/features/ims/index.js';
import '#core/features/analytics/gtm/index.js';
import '#core/features/analytics/gtag/index.js';
import '#core/features/analytics/google-analytics-4/index.js';
import '#core/features/analytics/facebook-pixel/index.js';
import '#core/features/codesnippets/index.js';
import '#core/features/affiliates/index.js';
import '#core/features/affiliate-bet-sharing/index.js';
import '#core/features/deep-linking/index.js';
import '#core/features/experimental-banners/index.js';
import {PortalSportsSegmentation} from '#core/features/sports-segmentation/index.js';

import {actionsProxyStorage} from '#core/application/api/actions-proxy.js';
import {AnalyticsProxy} from './application/api/analytics-proxy.js';
import {handle32010message, setUpdateBalancesListener} from '#core/application/api/balances-proxy/index.js';
import {Bonuses} from '#core/application/modules/bonuses/bonuses.js';
import {setMetaDataListener} from '#core/application/api/seo-proxy.js';
import {navigate, setInternalRouteListener} from '#core/application/api/router-proxy.js';
import {apiLog} from '#core/application/api/utils.js';
import {DrivenSSOService} from '#core/application/embedded-app/driven-sso-service.js';

import {MozaicEditor} from '#core/features/mozaic/index.js';
import {countAndIncreaseAutomatedPropertiesCount} from '#core/utils/statistics.js';
import {Intents} from '#core/application/intents/index.js';
import {DBXPerformanceService} from './services/performance-reporting-service';
import {setAppLoadedListener} from '#core/application/application-loaded-notifier.js';
import {getVisionContentTransporter} from '#core/application/application.vision.js';

const log = Logger('DBX');

const {bootstrap: mojitoBootstrap, SubscriptionResolver} = MojitoApplications.Sports;
const IntentActions = MojitoCore.Intents.actions;
const UserSettingsActions = MojitoServices.UserSettings.actions;
const UserSettingsSelectors = MojitoServices.UserSettings.selectors;
const {dispatch} = MojitoCore.Services.redux.store;

let enrichMojitoConfigWithCustomFn;
let getRuntimeConfigFn;
let customMessageHandler;

function registerSportsbookWidgets() {
    WidgetsProviderService.registerWidget('bet-history', 'widget-bet-history', () =>
        import(
            /* webpackChunkName: "widget.bet-history" */
            '#core/widgets/widget-bet-history.jsx'
        ).then(module => module.WidgetBetHistory)
    );
    WidgetsProviderService.registerWidget('bet-details', 'widget-bet-details', () =>
        import(
            /* webpackChunkName: "widget.bet-details" */
            '#core/widgets/widget-bet-details.jsx'
        ).then(module => module.WidgetBetDetails)
    );
    WidgetsProviderService.registerWidget('bonus-history', 'widget-bonus-history', () =>
        import(
            /* webpackChunkName: "widget.bonus-history" */
            '#core/widgets/widget-bonus-history.jsx'
        ).then(module => module.WidgetBonusHistory)
    );
    WidgetsProviderService.registerWidget('active-bonuses', 'widget-active-bonuses', () =>
        import(
            /* webpackChunkName: "widget.active-bonuses" */
            '#core/widgets/widget-active-bonuses.jsx'
        ).then(module => module.WidgetActiveBonuses)
    );
    WidgetsProviderService.registerWidget('in-progress-bonuses', 'widget-in-progress-bonuses', () =>
        import(
            /* webpackChunkName: "widget.in-progress-bonuses" */
            '#core/widgets/widget-in-progress-bonuses.jsx'
        ).then(module => module.WidgetInProgressBonuses)
    );
    WidgetsProviderService.registerWidget('selection-button', null, () =>
        import(
            /* webpackChunkName: "widget.selection-button" */
            '#core/widgets/widget-selection-button.jsx'
        ).then(module => module.WidgetSelectionButton)
    );
}

export function configureSportsbook(conf) {
    getRuntimeConfigFn = conf.getRuntimeConfigFn;
    customMessageHandler = conf.customMessageHandler;
    enrichMojitoConfigWithCustomFn = conf.enrichMojitoConfigWithCustomFn;

    const bundledConfig = conf.configuration || {};
    if (conf.getTokensFn) {
        const tokens = conf.getTokensFn();

        // ---- Store tokens into config. TODO: everything should be passed via config
        for (const id in tokens.palette || {}) {
            const value = tokens.palette[id];
            setObjectProperty(bundledConfig, id, value);
        }
        for (const id in tokens.tile || {}) {
            const value = tokens.tile[id];
            setObjectProperty(bundledConfig, id, value);
        }

        const mojCustomBundledStyles = {};
        const customs = tokens.custom;
        for (const id in customs) {
            assignRecursively(mojCustomBundledStyles, customs[id]);
            countAndIncreaseAutomatedPropertiesCount(id, 0, customs[id]);
        }
        mergeTo(bundledConfig, {mojitoConfiguration: {services: {configurations: mojCustomBundledStyles}}});
    }

    ApplicationConfig.setBundledConfig(bundledConfig);
    ApplicationConfig.updateInitParams({
        configNameToLoad: 'config', // external config name from where to load configuration file, by default its config.json
        postInitConfig: undefined,
        cookieConsentGiven: false,
        routingPrefix: '/',
        applicationPlaneZIndex: 9,
        overlayPlaneZIndex: 1000,
        containerId: 'app',
        overlayContainerId: undefined,
        currencyCode: undefined,
        uiContext: undefined,
        language: undefined,
        authMethod: 'external',
    });

    registerSportsbookWidgets();
}

class SportsbookSubscriptionResolver extends AbstractFeature {
    constructor(parent) {
        super(parent);

        this.transporter = null;
        this.subscriptionResolver = null;
    }

    async beforeMojito() {
        this.subscriptionResolver = new SubscriptionResolver();

        if (NO_CDS) {
            const neverEndingPromise = new Promise(() => {});
            const NULL_TRANSPORTER = {
                init: () => {},
                registerCollection: () => {},
                subscribe: () => {
                    return {promise: neverEndingPromise};
                },
                subscribeMultiple: () => {
                    return {promise: neverEndingPromise};
                },
                search: () => {},
                dispose: () => {},
            };

            this.transporter = NULL_TRANSPORTER;

            return;
        }

        if (IS_VISION) {
            const visionTransporterStartTime = performance.now(); // Shared start time
            log.info(`%c[Vision Performance] transporter start time: ${Date.now()}`, Logger.STYLE_PROGRESS);

            log.info(
                `%c[Vision Performance] transporter started since page started loading: ${visionTransporterStartTime}ms`,
                Logger.STYLE_PROGRESS
            );
            const visionContentTransporter = await getVisionContentTransporter({
                subscriptionResolver: this.subscriptionResolver,
            });
            const visionTransporterResolvedTime = performance.now();

            log.info(
                `%c[Vision Performance] transporter resolved since page started loading: ${visionTransporterResolvedTime}ms and took ${visionTransporterResolvedTime - visionTransporterStartTime}ms`,
                Logger.STYLE_PROGRESS
            );

            this.transporter = visionContentTransporter;

            return;
        }

        const NgenContentTransporter = MojitoNGEN.NgenContentTransporter;
        const wsStatusListener = MojitoCore.Services.Performance.wsStatusListener;

        this.transporter = new NgenContentTransporter({
            wsStatusListener,
            maxConcurrentRequests: 30,
            baseUrl: ApplicationConfig.apiUrl,
        });
    }

    updateMojitoConfig(mojitoConfig) {
        mojitoConfig.application.subscriptionResolver = this.subscriptionResolver;
        mojitoConfig.core.contentTransporter = this.transporter;
    }
}

const SubscriptionResolverFeature = new SportsbookSubscriptionResolver(allFeatures);

const {INIT_NONE} = INITIALIZATION_STATE;

const state = {
    initializationState: INIT_NONE,
    initializationPromise: null,
    uid: '',
    overlayId: '',

    containerNode: null,
    stylesNode: null,
    reactAppNode: null,
    overlayContainerNode: null, // we do not own it
    overlayNode: null, // we own it

    isSetMetaData: false,
};

const externalHandlers = Object.create(null);
let performanceLogHandler = null;

function wrapExternal(id) {
    return function () {
        apiLog.debug(id, ...arguments);
        const fn = externalHandlers[id];
        if (!fn) {
            // do nothing
            apiLog.warn(`External method "${id}" is not set. Doing nothing`);

            return;
        }
        try {
            return fn(...arguments);
        } catch (e) {
            apiLog.warn(`Failed to call external method "${id}": ${e.message}`);
        }
    };
}

window.Sportsbook = {
    ...(window.Sportsbook || {}),

    init: function (options = {}) {
        apiLog.debug('init', ...arguments);
        Sportsbook.isEmbedded = !options.standalone;

        state.initializationPromise = init({
            log,
            state,
            _initParams: options,
            mojitoBootstrap,
            enrichMojitoConfigWithCustomFn,
            getRuntimeConfigFn,
        });

        return state.initializationPromise;
    },

    terminate: function () {
        apiLog.debug('terminate', ...arguments);
        try {
            setUpdateBalancesListener(null);
            setMetaDataListener(null);

            return terminate({log, state, mojitoBootstrap});
        } catch (e) {
            return false;
        }
    },

    dispose() {
        this.terminate();
    },

    sendMessage: function (id, data) {
        apiLog.debug('sendMessage', ...arguments);
        try {
            switch (id) {
                case API_MESSAGES.SYNC_SESSION:
                    return syncSession(data).then(() => {
                        if (data) {
                            const currentGetTagsHandler = externalHandlers[EXTERNAL_METHODS.IMS_GET_TAGS];
                            if (currentGetTagsHandler) {
                                apiLog.debug(
                                    `Triggering IMS_GET_TAGS to Portal after successful SYNC_SESSION (login).`
                                );
                                currentGetTagsHandler([IMS_TYPES.PLAYER_TAGS.SPORT_RANK_ONE]);
                            }
                        }
                    });
                case API_MESSAGES.CHANGE_ODDS_FORMAT: {
                    const supportedOddsFormats = UserSettingsSelectors.selectAvailableOddsFormats();

                    if (!Object.values(supportedOddsFormats).includes(data)) {
                        log.error(`Invalid odds format: ${data}`);

                        return Promise.reject();
                    }
                    dispatch(UserSettingsActions.updateOddsFormat(data));

                    return Promise.resolve();
                }
                case API_MESSAGES.GET_ODDS_FORMAT: {
                    return Promise.resolve(UserSettingsSelectors.selectOddsFormat());
                }
                case API_MESSAGES.OAPI_RESPONSE: {
                    switch (data.ID) {
                        case 32010:
                            handle32010message(data, !Bonuses.useIMSFreebetBalance);
                            break;
                    }

                    return Promise.resolve();
                }
                case API_MESSAGES.IMS_SET_TAGS:
                    PortalSportsSegmentation.handleSetTagsMessage(data);

                    return Promise.resolve();
                case API_MESSAGES.IMS_UPDATE_TAGS:
                    PortalSportsSegmentation.handleUpdateTagsMessage(data);

                    return Promise.resolve();
                case API_MESSAGES.SET_ADDITIONAL_SITUATION_CONTEXT_IDS:
                    SubscriptionResolverFeature.transporter?.setAdditionalSituationContextIds
                        ? SubscriptionResolverFeature.transporter.setAdditionalSituationContextIds(data)
                        : apiLog.warn(
                              'ContentTransporter does not exist or does not support setAdditionalSituationContextIds'
                          );

                    return Promise.resolve();
                case API_MESSAGES.CUSTOM:
                    customMessageHandler && customMessageHandler(data);

                    return Promise.resolve();
                default:
                    return sendMessage(log, id, data);
            }
        } catch (e) {
            log.error(`Failed to invoke method ${id}`, e);
        }
    },

    // Handler can be changed in runtime
    setListener: function (id, fn) {
        apiLog.debug('setListener', ...arguments);
        externalHandlers[id] = fn;
        try {
            switch (id) {
                case EXTERNAL_METHODS.SHOW_LOGIN:
                    actionsProxyStorage.setShowLoginHandler(fn ? wrapExternal(id) : null);
                    break;
                case EXTERNAL_METHODS.PAGE_METATAGS_UPDATED:
                    setMetaDataListener(fn ? wrapExternal(id) : null);
                    break;
                case EXTERNAL_METHODS.SHOW_DEPOSIT:
                    actionsProxyStorage.setDepositHandler(fn ? wrapExternal(id) : null);
                    break;
                case EXTERNAL_METHODS.NAVIGATE:
                    actionsProxyStorage.setNavigateHostHandler(fn ? wrapExternal(id) : null);
                    break;
                case EXTERNAL_METHODS.BALANCE_UPDATED:
                    setUpdateBalancesListener(fn ? wrapExternal(id) : null);
                    break;
                case EXTERNAL_METHODS.ANALYTICS_EVENT:
                    AnalyticsProxy.setHandler(fn ? wrapExternal(id) : null);
                    break;
                case EXTERNAL_METHODS.GET_SESSION_INFO:
                    DrivenSSOService.setCredentialsRetriever(fn ? wrapExternal(id) : null);
                    break;
                case EXTERNAL_METHODS.LOG_OUT:
                    actionsProxyStorage.setLogoutHandler(fn ? wrapExternal(id) : null);
                    break;
                case EXTERNAL_METHODS.PERFORMANCE_LOG:
                    if (fn) {
                        DBXPerformanceService.removeLogger(performanceLogHandler);
                        performanceLogHandler = fn;
                        DBXPerformanceService.addLogger(performanceLogHandler);
                    } else {
                        DBXPerformanceService.removeLogger(performanceLogHandler);
                        performanceLogHandler = null;
                    }
                    break;
                case EXTERNAL_METHODS.APPLICATION_LOADED:
                case EXTERNAL_METHODS.SPORTSBOOK_LOADED:
                    externalHandlers[EXTERNAL_METHODS.APPLICATION_LOADED] = fn;
                    setAppLoadedListener(fn);
                    break;
                case EXTERNAL_METHODS.INTERNAL_ROUTE:
                    setInternalRouteListener(fn);
                    break;

                case EXTERNAL_METHODS.IMS_GET_TAGS:
                    break;

                default:
                    log.warn(`Sportsbook.setListener: Unknown event id: ${id}`);

                    return false;
            }

            return true;
        } catch (e) {
            log.error(e);

            return false;
        }
    },

    mountSportsbook(containerId, params = {}) {
        apiLog.debug('mountSportsbook', ...arguments);
        const result = mount(containerId, params, {
            log,
            state,
            mojitoBootstrap,
        });
        if (result && Intents.SPORTSBOOK_MOUNTED) {
            dispatch(IntentActions.publishIntent(Intents.SPORTSBOOK_MOUNTED.type, Intents.SPORTSBOOK_MOUNTED.data));
        }

        return result;
    },

    unmountSportsbook() {
        apiLog.debug('unmountSportsbook', ...arguments);

        return unmount({log, state, mojitoBootstrap});
    },

    getWidgetByCurrentRoute: function () {
        return WidgetsProviderService.getWidgetByCurrentRoute();
    },

    mountWidget: function (widgetId, regionId, params) {
        apiLog.debug('mountWidget', ...arguments);

        if (widgetId === 'sportsbook') return this.mountSportsbook(regionId, params);

        return WidgetsProviderService.mountWidget(widgetId, regionId, params);
    },

    unmountWidget: function (regionId) {
        apiLog.debug('unmountWidget', ...arguments);

        if (regionId === 'sportsbook') return this.unmountSportsbook();

        return WidgetsProviderService.unmountWidget(regionId);
    },

    navigate: function (method, ...params) {
        return navigate(state, method, ...params);
    },

    showMozaic(params) {
        MozaicEditor.show(params);
    },

    get initializationState() {
        return state.initializationState;
    },

    get initializationPromise() {
        return state.initializationPromise;
    },

    get isMounted() {
        return !!state.containerNode;
    },

    get language() {
        return ApplicationConfig.initParams.language;
    },

    get usedLanguage() {
        return Localization.getCurrentLanguage();
    },

    get availableLanguages() {
        return Localization.getAvailableLanguages();
    },

    get _state() {
        return Object.assign({}, state);
    },

    get _config() {
        return Object.assign({}, ApplicationConfig.initParams);
    },

    get _routingPrefix() {
        return ApplicationConfig.initParams.routingPrefix;
    },
};

window.DBX_APPLICATION = window.Sportsbook;
